import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, Check, X, Lock } from 'lucide-react';

import { API_BASE as API_BASE_URL } from '../api/config';

axios.defaults.withCredentials = true;

const UserManagementHeader = React.memo(({ onAdd }) => (
    <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        padding: '32px 28px',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 107, 53, 0.3)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2), 0 4px 12px rgba(255, 107, 53, 0.15)',
        position: 'relative',
        overflow: 'hidden'
    }}>
        {/* Decorative background gradient */}
        <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '400px',
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(255, 107, 53, 0.12), transparent 70%)',
            pointerEvents: 'none'
        }}></div>

        <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
                {/* Icon */}
                <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(255, 107, 53, 0.35)',
                }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8.5" cy="7" r="4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M20 8V14M17 11H23" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <h1 style={{
                    fontSize: '38px',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, #fff 0%, #e2e8f0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    margin: 0,
                    letterSpacing: '-0.5px',
                    fontFamily: "'Segoe UI', 'Arial', sans-serif"
                }}>
                    ErpAnalyticsUsers Management
                </h1>
            </div>
            <p style={{
                color: 'rgba(226, 232, 240, 0.85)',
                fontSize: '15px',
                fontWeight: '500',
                margin: 0,
                marginLeft: '72px',
                fontFamily: "'Segoe UI', 'Arial', sans-serif",
                letterSpacing: '0.2px'
            }}>
                Manage system access, roles, and ErpAnalyticsUsers permissions
            </p>
        </div>
        <button
            onClick={onAdd}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '14px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)',
                transition: 'all 0.3s ease',
                fontFamily: "'Segoe UI', 'Arial', sans-serif",
                letterSpacing: '0.3px',
                position: 'relative',
                zIndex: 1
            }}
            onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 10px 30px rgba(255, 107, 53, 0.6)';
            }}
            onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.4)';
            }}
        >
            <UserPlus size={22} />
            Add ErpAnalyticsUser
        </button>
    </div>
));

const UsersTable = React.memo(({ users, onEdit, onDelete }) => (
    <div style={{
        overflowX: 'auto',
        background: 'linear-gradient(135deg, #1a2332 0%, #2a3f5f 50%, #1e293b 100%)',
        borderRadius: '16px',
        border: '2px solid #ff6b35',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
        padding: '8px'
    }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr style={{
                    background: 'linear-gradient(135deg, #1a2332 0%, #2a3f5f 50%, #1e293b 100%)',
                    borderBottom: '2px solid #ff6b35'
                }}>
                    <th style={{
                        padding: '20px',
                        textAlign: 'left',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderRight: '2px solid rgba(255, 107, 53, 0.3)'
                    }}>Status</th>
                    <th style={{
                        padding: '20px',
                        textAlign: 'left',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderRight: '2px solid rgba(255, 107, 53, 0.3)'
                    }}>Username</th>
                    <th style={{
                        padding: '20px',
                        textAlign: 'left',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderRight: '2px solid rgba(255, 107, 53, 0.3)'
                    }}>Role</th>
                    <th style={{
                        padding: '20px',
                        textAlign: 'left',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderRight: '2px solid rgba(255, 107, 53, 0.3)'
                    }}>Linked Person</th>
                    <th style={{
                        padding: '20px',
                        textAlign: 'left',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {users.map((user, index) => (
                    <tr
                        key={user?.UserID ?? `user-${index}`}
                        style={{
                            borderBottom: '2px solid rgba(255, 107, 53, 0.2)',
                            background: index % 2 === 0
                                ? 'rgba(26, 35, 50, 0.6)'
                                : 'rgba(30, 41, 59, 0.6)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(255, 133, 85, 0.1) 100%)';
                            e.currentTarget.style.transform = 'scale(1.01)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = index % 2 === 0
                                ? 'rgba(26, 35, 50, 0.6)'
                                : 'rgba(30, 41, 59, 0.6)';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        <td style={{ padding: '18px 20px', borderRight: '2px solid rgba(255, 107, 53, 0.2)' }}>
                            {user.IsActive ? (
                                <span style={{
                                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(22, 163, 74, 0.3) 100%)',
                                    color: '#4ade80',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    border: '3px solid #22c55e',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 4px 10px rgba(34, 197, 94, 0.3)'
                                }}>
                                    <Check size={14} /> Active
                                </span>
                            ) : (
                                <span style={{
                                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.3) 100%)',
                                    color: '#f87171',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    border: '3px solid #ef4444',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)'
                                }}>
                                    <X size={14} /> Inactive
                                </span>
                            )}
                            {user.AccountLockedUntil && (
                                <span style={{
                                    marginTop: '8px',
                                    background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(234, 88, 12, 0.3) 100%)',
                                    color: '#fb923c',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    border: '3px solid #f97316',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 4px 10px rgba(251, 146, 60, 0.3)'
                                }}>
                                    <Lock size={14} /> Locked
                                </span>
                            )}
                        </td>
                        <td style={{
                            padding: '18px 20px',
                            fontWeight: '700',
                            color: '#ffffff',
                            fontSize: '15px',
                            borderRight: '2px solid rgba(255, 107, 53, 0.2)'
                        }}>
                            {user.Username}
                        </td>
                        <td style={{ padding: '18px 20px', borderRight: '2px solid rgba(255, 107, 53, 0.2)' }}>
                            <span style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: '800',
                                border: '2px solid',
                                display: 'inline-block',
                                ...(user.Role === 'IT_Admin' ? {
                                    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.3) 0%, rgba(255, 133, 85, 0.3) 100%)',
                                    color: '#ff8a3d',
                                    borderColor: '#ff6b35',
                                    boxShadow: '0 4px 10px rgba(255, 107, 53, 0.4)'
                                } : user.Role === 'Admin' ? {
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(37, 99, 235, 0.3) 100%)',
                                    color: '#60a5fa',
                                    borderColor: '#3b82f6',
                                    boxShadow: '0 4px 10px rgba(59, 130, 246, 0.4)'
                                } : {
                                    background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.3) 0%, rgba(75, 85, 99, 0.3) 100%)',
                                    color: '#d1d5db',
                                    borderColor: '#6b7280',
                                    boxShadow: '0 4px 10px rgba(107, 114, 128, 0.3)'
                                })
                            }}>
                                {user.Role || <span style={{ color: '#64748b', fontStyle: 'italic' }}>-</span>}
                            </span>
                        </td>
                        <td style={{
                            padding: '18px 20px',
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '15px',
                            fontWeight: '600',
                            borderRight: '2px solid rgba(255, 107, 53, 0.2)'
                        }}>
                            {user.LinkedSalesPersonName || <span style={{ color: '#64748b', fontStyle: 'italic' }}>-</span>}
                        </td>
                        <td style={{ padding: '18px 20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => onEdit(user)}
                                style={{
                                    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.3) 0%, rgba(255, 133, 85, 0.3) 100%)',
                                    color: '#ff8a3d',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: '2px solid #ff6b35',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 10px rgba(255, 107, 53, 0.4)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)';
                                    e.target.style.color = '#ffffff';
                                    e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, rgba(255, 107, 53, 0.3) 0%, rgba(255, 133, 85, 0.3) 100%)';
                                    e.target.style.color = '#ff8a3d';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                Edit
                            </button>
                            {onDelete && (
                                <button
                                    onClick={() => {
                                        if (window.confirm(`Delete user "${user.Username}"? This cannot be undone.`)) {
                                            onDelete(user);
                                        }
                                    }}
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.25)',
                                        color: '#f87171',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        border: '2px solid #ef4444',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ef4444';
                                        e.target.style.color = '#ffffff';
                                        e.target.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(239, 68, 68, 0.25)';
                                        e.target.style.color = '#f87171';
                                        e.target.style.transform = 'scale(1)';
                                    }}
                                >
                                    Delete
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
));

const UserModal = React.memo(({
    showAddModal,
    showEditModal,
    selectedUser,
    formData,
    setFormData,
    salespeopleOptions,
    onClose,
    onSubmit,
    onDelete,
}) => {
    if (!showAddModal && !showEditModal) return null;
    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a2332 0%, #2a3f5f 50%, #1e293b 100%)',
                color: '#ffffff',
                padding: '32px',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '500px',
                border: '2px solid #ff6b35',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{
                    fontSize: '28px',
                    fontWeight: '900',
                    marginBottom: '24px',
                    color: '#ff8a3d'
                }}>
                    {showAddModal ? " Add New ErpAnalyticsUser" : `️ Edit ErpAnalyticsUser ${selectedUser?.Username}`}
                </h2>

                <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {showAddModal && (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                color: '#cbd5e1',
                                marginBottom: '8px',
                                fontWeight: '700'
                            }}>
                                Username
                            </label>
                            <input
                                type="text"
                                style={{
                                    width: '100%',
                                    background: 'rgba(19, 22, 32, 0.8)',
                                    border: '2px solid rgba(255, 107, 53, 0.5)',
                                    borderRadius: '10px',
                                    padding: '12px 16px',
                                    color: '#ffffff',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    outline: 'none',
                                    transition: 'all 0.3s ease'
                                }}
                                value={formData.Username}
                                onChange={e => setFormData({ ...formData, Username: e.target.value })}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#ff6b35';
                                    e.target.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.5)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'rgba(255, 107, 53, 0.5)';
                                    e.target.style.boxShadow = 'none';
                                }}
                                required
                            />
                        </div>
                    )}

                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            color: '#cbd5e1',
                            marginBottom: '8px',
                            fontWeight: '700'
                        }}>
                            {showEditModal ? "Reset Password (leave empty to keep)" : "Password"}
                        </label>
                        <input
                            type="password"
                            style={{
                                width: '100%',
                                background: 'rgba(19, 22, 32, 0.8)',
                                border: '2px solid rgba(255, 107, 53, 0.5)',
                                borderRadius: '10px',
                                padding: '12px 16px',
                                color: '#ffffff',
                                fontSize: '15px',
                                fontWeight: '600',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            value={formData.Password}
                            onChange={e => setFormData({ ...formData, Password: e.target.value })}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#ff6b35';
                                e.target.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.5)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 107, 53, 0.5)';
                                e.target.style.boxShadow = 'none';
                            }}
                            required={showAddModal}
                        />
                    </div>

                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            color: '#cbd5e1',
                            marginBottom: '8px',
                            fontWeight: '700'
                        }}>
                            Role
                        </label>
                        <select
                            style={{
                                width: '100%',
                                background: 'rgba(19, 22, 32, 0.8)',
                                border: '3px solid rgba(255, 107, 53, 0.5)',
                                borderRadius: '10px',
                                padding: '12px 16px',
                                color: '#ffffff',
                                fontSize: '15px',
                                fontWeight: '600',
                                outline: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            value={formData.Role}
                            onChange={e => {
                                const newRole = e.target.value;
                                setFormData({
                                    ...formData,
                                    Role: newRole,
                                    AssignedToSalesPerson: newRole === 'Sales_Coordinator' ? (formData.LinkedSalesPersonName || '') : ''
                                });
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#ff6b35';
                                e.target.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.5)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 107, 53, 0.5)';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <option value="Sales_Person">Sales Person</option>
                            <option value="Sales_Coordinator">Sales Coordinator</option>
                            <option value="Management">Management</option>
                            <option value="Admin">Admin</option>
                            <option value="IT_Admin">IT Admin</option>
                        </select>
                    </div>

                    {(formData.Role === 'Sales_Person' || formData.Role === 'Sales_Coordinator') && (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                color: '#cbd5e1',
                                marginBottom: '8px',
                                fontWeight: '700'
                            }}>
                                {formData.Role === 'Sales_Coordinator'
                                    ? 'Sales Person (linked to)'
                                    : 'Linked Sales Person Name (Select from dropdown)'}
                            </label>
                            <select
                                style={{
                                    width: '100%',
                                    background: 'rgba(19, 22, 32, 0.8)',
                                    border: '3px solid rgba(255, 107, 53, 0.5)',
                                    borderRadius: '10px',
                                    padding: '12px 16px',
                                    color: '#ffffff',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                value={formData.LinkedSalesPersonName}
                                onChange={e => setFormData({
                                    ...formData,
                                    LinkedSalesPersonName: e.target.value,
                                    ...(formData.Role === 'Sales_Coordinator' && { AssignedToSalesPerson: e.target.value })
                                })}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#ff6b35';
                                    e.target.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.5)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'rgba(255, 107, 53, 0.5)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <option value="">-- Select Sales Person --</option>
                                {salespeopleOptions}
                            </select>
                        </div>
                    )}

                    {showEditModal && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginTop: '16px',
                            padding: '16px',
                            background: 'rgba(255, 107, 53, 0.1)',
                            borderRadius: '12px',
                            border: '2px solid rgba(255, 107, 53, 0.3)'
                        }}>
                            <input
                                type="checkbox"
                                checked={formData.IsActive}
                                onChange={e => setFormData({ ...formData, IsActive: e.target.checked })}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    cursor: 'pointer',
                                    accentColor: '#ff6b35'
                                }}
                            />
                            <label style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff', cursor: 'pointer' }}>
                                Account Active
                            </label>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {showEditModal && onDelete && selectedUser && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm(`Delete user "${selectedUser.Username}"? This cannot be undone.`)) {
                                            onDelete(selectedUser);
                                        }
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        color: '#f87171',
                                        background: 'rgba(239, 68, 68, 0.2)',
                                        border: '2px solid #ef4444',
                                        borderRadius: '10px',
                                        fontSize: '15px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#ef4444';
                                        e.target.style.color = '#ffffff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                        e.target.style.color = '#f87171';
                                    }}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    padding: '12px 24px',
                                    color: '#cbd5e1',
                                    background: 'rgba(107, 114, 128, 0.2)',
                                    border: '3px solid rgba(107, 114, 128, 0.5)',
                                    borderRadius: '10px',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#ffffff';
                                    e.target.style.borderColor = '#6b7280';
                                    e.target.style.background = 'rgba(107, 114, 128, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#cbd5e1';
                                    e.target.style.borderColor = 'rgba(107, 114, 128, 0.5)';
                                    e.target.style.background = 'rgba(107, 114, 128, 0.2)';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                style={{
                                    padding: '12px 28px',
                                    background: 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)',
                                    color: 'white',
                                    border: '3px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.5)';
                                }}
                            >
                                 Save Changes
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [salespeople, setSalespeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [formData, setFormData] = useState({
        Username: '',
        Password: '',
        Role: 'Sales_Person',
        LinkedSalesPersonName: '',
        AssignedToSalesPerson: ''
    });

    const fetchSalespeople = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/salespeople`, {
                withCredentials: true
            });

            const salespeopleList = response.data.salespeople || response.data.salespersons || [];

            const names = salespeopleList.map(sp => {
                if (typeof sp === 'string') return sp;
                return sp.name || sp;
            }).filter(name => name && name.trim() !== '');

            setSalespeople(names);
        } catch (err) {
            console.error("Error loading salespeople:", err);
            setSalespeople([]);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/users/`, {
                withCredentials: true
            });

            const data = response.data;
            const rawList = Array.isArray(data) ? data : (data?.users ?? data?.data ?? []);
            const list = Array.isArray(rawList) ? rawList : [];

            // Backend may return users as Python repr strings (e.g. "UserID=1 Username='admin' ...")
            const parseUserRepr = (s) => {
                if (typeof s !== 'string') return s;
                const obj = {};
                const re = /(\w+)=(None|True|False|\d+|'[^']*'|"[^"]*")/g;
                let m;
                while ((m = re.exec(s)) !== null) {
                    let val = m[2];
                    if (val === 'None') obj[m[1]] = null;
                    else if (val === 'True') obj[m[1]] = true;
                    else if (val === 'False') obj[m[1]] = false;
                    else if (/^\d+$/.test(val)) obj[m[1]] = parseInt(val, 10);
                    else if ((val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"'))) obj[m[1]] = val.slice(1, -1);
                    else obj[m[1]] = val;
                }
                return obj;
            };
            const listAsObjects = list.map((item) => (typeof item === 'string' && item.includes('UserID=') ? parseUserRepr(item) : item));

            const normalizeUser = (u) => {
                if (!u || typeof u !== 'object') return u;
                const id = u.UserID ?? u.user_id ?? u.userId ?? u.id;
                const username = u.Username ?? u.username ?? u.user_name ?? u.UserName ?? '';
                const role = u.Role ?? u.role ?? u.role_name ?? '';
                const isActiveRaw = u.IsActive ?? u.is_active ?? u.isActive;
                const isActive = (isActiveRaw === false || isActiveRaw === 0 || isActiveRaw === 'false' || isActiveRaw === '0') ? false : true;
                const linked = u.LinkedSalesPersonName ?? u.linked_sales_person_name ?? u.linked_sales_person ?? u.linkedSalesPerson ?? '';
                const assigned = u.AssignedToSalesPerson ?? u.assigned_to_sales_person ?? u.assigned_to ?? u.assignedTo ?? '';
                return {
                    ...u,
                    UserID: id,
                    Username: String(username ?? ''),
                    Role: String(role ?? ''),
                    IsActive: isActive,
                    LinkedSalesPersonName: String(linked ?? ''),
                    AssignedToSalesPerson: String(assigned ?? ''),
                    AccountLockedUntil: u.AccountLockedUntil ?? u.account_locked_until
                };
            };
            const normalized = listAsObjects.map(normalizeUser);
            if (normalized.length > 0 && !(normalized[0]?.Username || '').trim()) {
                console.warn('[UserManagement] Usernames empty after normalize. Raw first row keys:', Object.keys(listAsObjects[0]));
                console.warn('[UserManagement] Raw first user:', JSON.stringify(listAsObjects[0], null, 2));
            }
            setUsers(normalized);
        } catch (err) {
            console.error("Error fetching users:", err);
            if (err.response?.status === 401) {
                setError("Authentication failed. Please log out and log in again.");
            } else if (err.response?.status === 403) {
                setError("Access Denied: You must be IT Admin or Admin to manage users");
            } else {
                setError(err.response?.data?.detail || err.message || "Failed to fetch users");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                const authCheck = await axios.get(`${API_BASE_URL}/auth/me`, {
                    withCredentials: true
                });

                if (authCheck.data) {
                    fetchUsers();
                    fetchSalespeople();
                } else {
                    setError("Authentication failed. Please log out and log in again.");
                    setLoading(false);
                }
            } catch (err) {
                console.error("Auth verification failed:", err);
                setError("Authentication failed. Please log out and log in again.");
                setLoading(false);
            }
        };

        verifyAuth();
    }, [fetchUsers, fetchSalespeople]);

    const handleCreateUser = useCallback(async (e) => {
        e.preventDefault();
        try {
            const csrfToken = localStorage.getItem('csrf_token') || '';
            const response = await fetch(`${API_BASE_URL}/api/users/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({ detail: "Failed to create user" }));
                throw new Error(err.detail || `Failed to create user: ${response.status}`);
            }

            setShowAddModal(false);
            await fetchUsers();
            resetForm();
            alert("ErpAnalyticsUser created successfully!");
        } catch (err) {
            console.error("Error creating user:", err);
            alert(err.message || "Failed to create ErpAnalyticsUser. Please try again.");
        }
    }, [formData, fetchUsers]);

    const handleUpdateUser = useCallback(async (e) => {
        e.preventDefault();
        try {
            if (!selectedUser || !selectedUser.UserID) {
                alert("Invalid ErpAnalyticsUser selected. Please try again.");
                return;
            }

            const updatePayload = {
                Role: formData.Role,
                IsActive: formData.IsActive,
                LinkedSalesPersonName: formData.LinkedSalesPersonName || null,
                AssignedToSalesPerson: formData.Role === 'Sales_Coordinator'
                    ? (formData.LinkedSalesPersonName || null)
                    : (formData.AssignedToSalesPerson || null)
            };

            if (formData.Password) {
                updatePayload.Password = formData.Password;
            }

            const csrfToken = localStorage.getItem('csrf_token') || '';
            const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.UserID}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify(updatePayload)
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({ detail: "Failed to update user" }));
                throw new Error(err.detail || `Failed to update user: ${response.status}`);
            }

            setShowEditModal(false);
            await fetchUsers();
            resetForm();
            alert("ErpAnalyticsUser updated successfully!");
        } catch (err) {
            console.error("Error updating user:", err);
            alert(err.message || "Failed to update ErpAnalyticsUser. Please try again.");
        }
    }, [formData, selectedUser, fetchUsers]);

    const resetForm = useCallback(() => {
        setFormData({
            Username: '',
            Password: '',
            Role: 'Sales_Person',
            LinkedSalesPersonName: '',
            AssignedToSalesPerson: '',
            IsActive: true
        });
        setSelectedUser(null);
    }, []);

    const handleDeleteUser = useCallback(async (user) => {
        if (!user || !user.UserID) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/users/${user.UserID}`, {
                withCredentials: true
            });
            if (selectedUser && selectedUser.UserID === user.UserID) {
                setShowEditModal(false);
                setSelectedUser(null);
                resetForm();
            }
            await fetchUsers();
            alert('User deleted.');
        } catch (err) {
            console.error('Error deleting user:', err);
            const status = err.response?.status;
            let msg = err.response?.data?.detail || err.response?.data?.message;
            if (!msg) {
                if (status === 405) msg = 'Delete not allowed (405). Backend may not support DELETE.';
                else if (status === 500) msg = 'Server error (500). The delete failed on the server. Please try again or contact your administrator.';
                else msg = err.message || 'Failed to delete user.';
            }
            alert(msg);
        }
    }, [selectedUser, fetchUsers, resetForm]);

    const openEdit = useCallback((user) => {
        setSelectedUser(user);
        const linked = user.LinkedSalesPersonName || user.AssignedToSalesPerson || '';
        setFormData({
            Username: user.Username,
            Password: '',
            Role: user.Role,
            IsActive: user.IsActive,
            LinkedSalesPersonName: linked,
            AssignedToSalesPerson: user.Role === 'Sales_Coordinator' ? linked : (user.AssignedToSalesPerson || '')
        });
        setShowEditModal(true);
    }, []);

    const salespeopleOptions = useMemo(() => {
        if (!salespeople || salespeople.length === 0) {
            return <option value="" disabled>No active salespersons found</option>;
        }
        return salespeople.map((sp, idx) => (
            <option key={idx} value={sp}>
                {sp}
            </option>
        ));
    }, [salespeople]);

    const sortedUsers = useMemo(() => {
        if (!Array.isArray(users)) return [];
        return [...users].sort((a, b) => (a.IsActive ? 0 : 1) - (b.IsActive ? 0 : 1));
    }, [users]);

    const handleOpenAdd = useCallback(() => {
        resetForm();
        setShowAddModal(true);
    }, [resetForm]);

    const handleCloseModal = useCallback(() => {
        setShowAddModal(false);
        setShowEditModal(false);
    }, []);

    if (loading) return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#ffffff', fontSize: '20px', fontWeight: 'bold' }}>
            Loading ErpAnalyticsUsers...
        </div>
    );
    if (error) return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ color: '#ff4444', fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>{error}</div>
            <button
                onClick={fetchUsers}
                style={{
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)'
                }}
            >
                Retry
            </button>
        </div>
    );

    const allUsernamesEmpty = sortedUsers.length > 0 && sortedUsers.every(u => !(u?.Username || '').trim());

    return (
        <div style={{
            padding: '32px',
            background: 'linear-gradient(135deg, #0f172a 0%, #1a2332 50%, #1e293b 100%)',
            minHeight: '100vh',
            color: '#ffffff'
        }}>
            {/* Header Section */}
            <UserManagementHeader onAdd={handleOpenAdd} />

            {allUsernamesEmpty && (
                <div style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '1px solid rgba(251, 191, 36, 0.5)',
                    borderRadius: '8px',
                    color: '#fcd34d',
                    fontSize: '14px'
                }}>
                    Usernames not showing? Open DevTools (F12) → Console and look for <strong>[UserManagement]</strong> to see the API field names, or check Network → <strong>api/users</strong> response and share one sample user object so the display can be fixed.
                </div>
            )}

            {/* Table Container */}
            <UsersTable users={sortedUsers} onEdit={openEdit} onDelete={handleDeleteUser} />

            {/* ADD / EDIT MODAL */}
            <UserModal
                showAddModal={showAddModal}
                showEditModal={showEditModal}
                selectedUser={selectedUser}
                formData={formData}
                setFormData={setFormData}
                salespeopleOptions={salespeopleOptions}
                onClose={handleCloseModal}
                onSubmit={showAddModal ? handleCreateUser : handleUpdateUser}
                onDelete={handleDeleteUser}
            />
        </div>
    );
};

export default UserManagement;
